"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import {
  Check,
  Cookie,
  CreditCard,
  ShieldCheck,
  SlidersHorizontal,
  X,
} from "lucide-react";

import {
  OPEN_COOKIE_SETTINGS_EVENT,
  readCookieConsent,
  writeCookieConsent,
} from "@/lib/cookie-consent";

type View = "summary" | "settings";

export function CookieBanner() {
  const [isOpen, setIsOpen] = useState(false);
  const [hasSavedChoice, setHasSavedChoice] = useState(false);
  const [view, setView] = useState<View>("summary");
  const [payments, setPayments] = useState(false);

  useEffect(() => {
    const stored = readCookieConsent();

    if (stored) {
      setHasSavedChoice(true);
      setPayments(stored.payments);
    } else {
      setIsOpen(true);
    }

    const openSettings = () => {
      const current = readCookieConsent();
      setPayments(current?.payments ?? false);
      setHasSavedChoice(Boolean(current));
      setView("settings");
      setIsOpen(true);
    };

    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, openSettings);
    return () =>
      window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, openSettings);
  }, []);

  const save = (allowPayments: boolean) => {
    writeCookieConsent(allowPayments);
    setPayments(allowPayments);
    setHasSavedChoice(true);
    setIsOpen(false);
    setView("summary");
  };

  if (!isOpen) return null;

  return (
    <section
      className="fixed inset-x-0 bottom-0 z-[100] p-3 sm:p-5"
      aria-label="Cookie-Einstellungen"
    >
      <div className="mx-auto max-w-6xl overflow-hidden rounded-2xl border border-slate-700/80 bg-[#071425]/[0.98] text-slate-100 shadow-[0_24px_80px_rgba(0,0,0,0.5)] backdrop-blur-xl">
        <div className="h-1 bg-gradient-to-r from-blue-600 via-sky-400 to-blue-700" />

        {view === "summary" ? (
          <div className="grid gap-6 p-5 sm:p-6 lg:grid-cols-[1fr_auto] lg:items-end lg:gap-10">
            <div className="flex gap-4">
              <div className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-blue-400/20 bg-blue-500/10 text-blue-300 sm:flex">
                <Cookie className="h-5 w-5" aria-hidden="true" />
              </div>

              <div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-[0.16em] text-blue-300">
                  Deine Privatsphäre
                </p>
                <h2 className="text-lg font-semibold tracking-tight text-white sm:text-xl">
                  Cookies bei DevPlaner
                </h2>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">
                  Wir verwenden notwendige Cookies für Anmeldung, Sicherheit und
                  deine Sitzung. Stripe wird nur aktiviert, wenn du Zahlungen
                  erlaubst. Deine Auswahl kannst du jederzeit ändern.
                </p>
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
                  <Link className="transition hover:text-white" href="/datenschutz">
                    Datenschutz
                  </Link>
                  <Link className="transition hover:text-white" href="/impressum">
                    Impressum
                  </Link>
                </div>
              </div>
            </div>

            <div className="grid min-w-0 gap-2 sm:grid-cols-3 lg:min-w-[510px]">
              <button
                type="button"
                onClick={() => save(false)}
                className="inline-flex min-h-11 items-center justify-center rounded-xl bg-slate-700 px-4 text-sm font-semibold text-white transition hover:bg-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
              >
                Nur notwendige
              </button>
              <button
                type="button"
                onClick={() => setView("settings")}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-slate-600 bg-transparent px-4 text-sm font-semibold text-slate-100 transition hover:border-slate-400 hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
              >
                <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
                Einstellungen
              </button>
              <button
                type="button"
                onClick={() => save(true)}
                className="inline-flex min-h-11 items-center justify-center rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white shadow-[0_8px_28px_rgba(37,99,235,0.28)] transition hover:bg-blue-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
              >
                Alle akzeptieren
              </button>
            </div>
          </div>
        ) : (
          <div className="p-5 sm:p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-300">
                  Privatsphäre verwalten
                </p>
                <h2 className="mt-1 text-xl font-semibold tracking-tight text-white">
                  Cookie-Einstellungen
                </h2>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">
                  Wähle selbst, welche Dienste DevPlaner verwenden darf.
                </p>
              </div>

              {hasSavedChoice && (
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="rounded-lg p-2 text-slate-400 transition hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
                  aria-label="Einstellungen schließen"
                >
                  <X className="h-5 w-5" aria-hidden="true" />
                </button>
              )}
            </div>

            <div className="mt-5 grid gap-3 md:grid-cols-2">
              <ConsentRow
                icon={<ShieldCheck className="h-5 w-5" aria-hidden="true" />}
                title="Notwendig"
                description="Login, Sitzung, Sicherheit und deine gespeicherte Cookie-Auswahl."
                badge="Immer aktiv"
              />

              <ConsentRow
                icon={<CreditCard className="h-5 w-5" aria-hidden="true" />}
                title="Zahlungen"
                description="Erlaubt Stripe für sichere Bezahlvorgänge und Betrugsprävention."
                checked={payments}
                onCheckedChange={setPayments}
              />
            </div>

            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex gap-4 text-xs text-slate-400">
                <Link className="transition hover:text-white" href="/datenschutz">
                  Datenschutz
                </Link>
                <Link className="transition hover:text-white" href="/impressum">
                  Impressum
                </Link>
              </div>

              <div className="grid gap-2 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => save(payments)}
                  className="inline-flex min-h-11 items-center justify-center rounded-xl bg-slate-700 px-5 text-sm font-semibold text-white transition hover:bg-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
                >
                  Auswahl speichern
                </button>
                <button
                  type="button"
                  onClick={() => save(true)}
                  className="inline-flex min-h-11 items-center justify-center rounded-xl bg-blue-600 px-5 text-sm font-semibold text-white transition hover:bg-blue-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
                >
                  Alle akzeptieren
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

type ConsentRowProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
  badge?: string;
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
};

function ConsentRow({
  icon,
  title,
  description,
  badge,
  checked = true,
  onCheckedChange,
}: ConsentRowProps) {
  const isInteractive = Boolean(onCheckedChange);

  return (
    <div className="flex min-h-[112px] items-start gap-3 rounded-xl border border-slate-700 bg-slate-900/50 p-4">
      <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-500/10 text-blue-300">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-semibold text-white">{title}</h3>
          {badge ? (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-400/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-300">
              <Check className="h-3 w-3" aria-hidden="true" />
              {badge}
            </span>
          ) : (
            <button
              type="button"
              role="switch"
              aria-checked={checked}
              aria-label={`${title} ${checked ? "deaktivieren" : "aktivieren"}`}
              onClick={() => onCheckedChange?.(!checked)}
              className={`relative h-7 w-12 shrink-0 rounded-full transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 ${
                checked ? "bg-blue-600" : "bg-slate-600"
              }`}
            >
              <span
                className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition ${
                  checked ? "left-6" : "left-1"
                }`}
              />
            </button>
          )}
        </div>
        <p className="mt-1.5 text-sm leading-5 text-slate-400">{description}</p>
        {!isInteractive && (
          <span className="sr-only">Diese Kategorie kann nicht deaktiviert werden.</span>
        )}
      </div>
    </div>
  );
}
